import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, List, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  NewRequestPageDocument,
  ProcurementSettingsPageDocument,
  ProcurementTypeItemFragment,
  RequestTypeDetailDocument,
  RequestTypeDetailQuery,
  ToggleProcurementTypeEnablementDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteAlertModal from 'modules/procurement/components/procurementTypes/DeleteAlertModal'
import DeleteRequestTypeModal from 'modules/procurement/components/procurementTypes/DeleteRequestTypeModal'
import EditRequestTypeModal from 'modules/procurement/components/procurementTypes/EditRequestTypeModal'

interface Props {
  requestType: ProcurementTypeItemFragment
}

const TypeItem = ({ requestType }: Props) => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const [editModalOpen, setEditModalOpen] = React.useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [deleteAlertModalOpen, setDeleteAlertModalOpen] = React.useState(false)

  const { data: featureFlagsData } = useQuery(RequestTypeDetailDocument, {
    variables: { companyId: company.id, requestTypeId: 0 },
    skip: !company?.id,
  })

  const isCustomFormsEnabled =
    featureFlagsData?.features?.featuresConnection?.nodes?.some(
      (feature: RequestTypeDetailQuery['features']['featuresConnection']['nodes'][0]) =>
        feature.name === 'dev_procurement_custom_forms' && feature.enabled
    ) || false

  const [toggleProcurementTypeEnablement] = useMutation(ToggleProcurementTypeEnablementDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementType,
  })

  const toggleTypeEnablement = async () => {
    const result = await toggleProcurementTypeEnablement({
      variables: { id: requestType.id, companyId: company.id, enabled: !requestType.enabled },
      refetchQueries: [ProcurementSettingsPageDocument, NewRequestPageDocument],
    })

    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    )
    return handled
  }

  const handleEdit = () => {
    navigate(`/settings/procurement/${requestType.id}`)
  }

  const countPurchaseRequestsPerType = requestType.purchaseRequestsConnection.totalCount || 0
  const isDeletable = countPurchaseRequestsPerType === 0

  return (
    <>
      <List.Item key={requestType.id}>
        <List.Title>{requestType.name}</List.Title>
        <List.ActionItem>
          <Box flexDirection="row" gap="s8">
            <TogglerCheckbox
              value={!!requestType.enabled}
              onChange={toggleTypeEnablement}
              disabled={requestType.identifier === 'other'}
            />
            {!requestType.identifier && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center">
                  <PopoverMenu.TouchableOption
                    title={i18n.t('procurement.purchase_requests.settings.types.actions.edit')}
                    onClick={() => setEditModalOpen(true)}
                  />
                  <PopoverMenu.TouchableOption
                    title={i18n.t('procurement.purchase_requests.settings.types.actions.delete')}
                    onClick={() =>
                      isDeletable ? setDeleteModalOpen(true) : setDeleteAlertModalOpen(true)
                    }
                    danger
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
            {isCustomFormsEnabled && (
              <Button type="secondary" onClick={handleEdit} iconName="pencil" />
            )}
          </Box>
        </List.ActionItem>
      </List.Item>
      {editModalOpen && (
        <EditRequestTypeModal requestType={requestType} onClose={() => setEditModalOpen(false)} />
      )}
      {deleteAlertModalOpen && <DeleteAlertModal onClose={() => setDeleteAlertModalOpen(false)} />}
      {deleteModalOpen && (
        <DeleteRequestTypeModal
          requestType={requestType}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </>
  )
}

export default TypeItem
